import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaPencilAlt,
  FaPlus,
  FaUndo,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import { useAppContext } from '../Store/Store';
import RichTextEditor from './RichTextEditor';
import './Management.css';

interface EventOption {
  id: number;
  name: string;
  code: string;
}

type AnswerType = 'open' | 'single' | 'multi';
type ProductStatus = 'active' | 'suspended' | 'deactivated';

interface EmployeeProduct {
  id: number;
  name: string;
  code: string;
  serial_number: string;
  redirect_url: string;
  product_indication: string;
  remaining_executions: number;
  status: ProductStatus;
  multianswer_selection: boolean;
  multianswer_allow_multiple: boolean;
  multianswer_show_free_textarea: boolean;
  multianswer_options?: string[];
  multianswer_dropdown_indication?: string;
  event_name?: string;
  event_id?: number;
  brand_name?: string;
  brand_id?: number;
  created_at?: string;
}

const ANSWER_TYPE_META: Record<AnswerType, { label: string; cls: string }> = {
  open: { label: 'Open', cls: 'mgmt-badge mgmt-badge--secondary' },
  single: {
    label: 'Closed - Single answer',
    cls: 'mgmt-badge mgmt-badge--primary',
  },
  multi: {
    label: 'Closed - Multi answer',
    cls: 'mgmt-badge mgmt-badge--purple',
  },
};

const STATUS_META: Record<ProductStatus, { label: string; cls: string }> = {
  active: { label: '● Active', cls: 'mgmt-badge mgmt-badge--success' },
  suspended: { label: '◐ Suspended', cls: 'mgmt-badge mgmt-badge--warning' },
  deactivated: {
    label: '○ Deactivated',
    cls: 'mgmt-badge mgmt-badge--secondary',
  },
};

function getAnswerType(
  p: Pick<
    EmployeeProduct,
    'multianswer_selection' | 'multianswer_allow_multiple'
  >
): AnswerType {
  if (!p.multianswer_selection) return 'open';
  return p.multianswer_allow_multiple ? 'multi' : 'single';
}

function answerTypeToFlags(type: AnswerType): {
  multianswer_selection: boolean;
  multianswer_allow_multiple: boolean;
} {
  if (type === 'single')
    return { multianswer_selection: true, multianswer_allow_multiple: false };
  if (type === 'multi')
    return { multianswer_selection: true, multianswer_allow_multiple: true };
  return { multianswer_selection: false, multianswer_allow_multiple: false };
}

interface PaginationInfo {
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

type SortKey =
  | 'name'
  | 'event'
  | 'code'
  | 'redirect_url'
  | 'answer_type'
  | 'created_at'
  | 'status';
type SortDir = 'asc' | 'desc';

const Products: React.FC = () => {
  const { user, backendBaseUrl } = useAppContext();

  const [products, setProducts] = useState<EmployeeProduct[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [events, setEvents] = useState<EventOption[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [filterStatus, setFilterStatus] = useState<
    '' | 'all' | 'active' | 'suspended' | 'deactivated'
  >('all');
  const [filterType, setFilterType] = useState<'' | AnswerType>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortKey | ''>('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);

  /* ── edit modal state ── */
  const [modalProduct, setModalProduct] = useState<EmployeeProduct | null>(
    null
  );
  const [editRedirectUrl, setEditRedirectUrl] = useState('');
  const [editIndication, setEditIndication] = useState('');
  const [editAnswerType, setEditAnswerType] = useState<AnswerType>('open');
  const [editMultianswerOptions, setEditMultianswerOptions] = useState<
    string[]
  >([]);
  const [editMultianswerDropdownIndication, setEditMultianswerDropdownIndication] = useState('');
  const [editFreeTextarea, setEditFreeTextarea] = useState(false);
  const [editStatus, setEditStatus] = useState<ProductStatus>('active');
  const [saving, setSaving] = useState(false);
  const firstInputRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  /* ── bulk edit state ── */
  const [bulkEdit, setBulkEdit] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);

  /* ── recover confirm modal state ── */
  const [recoverTarget, setRecoverTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [recovering, setRecovering] = useState(false);

  /* ── data fetching ── */
  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(
        `${backendBaseUrl}/employee/events/?user_id=${user.id}`
      );
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [backendBaseUrl, user?.id]);

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (user?.id) params.append('user_id', String(user.id));
      if (selectedEventId) params.append('event_id', selectedEventId);
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('answer_type', filterType);
      if (searchQuery) params.append('q', searchQuery);
      if (sortBy) {
        params.append('sort', sortBy);
        params.append('dir', sortDir);
      }
      params.append('page', String(currentPage));
      params.append('page_size', String(pageSize));
      const res = await axios.get(
        `${backendBaseUrl}/employee/products/?${params.toString()}`
      );
      setProducts(res.data.products);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    }
  }, [
    backendBaseUrl,
    user?.id,
    selectedEventId,
    filterStatus,
    filterType,
    searchQuery,
    sortBy,
    sortDir,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  /* ── edit modal helpers ── */
  const openModal = (product: EmployeeProduct) => {
    setBulkEdit(false);
    setModalProduct(product);
    setEditRedirectUrl(product.redirect_url);
    setEditIndication(product.product_indication);
    setEditAnswerType(getAnswerType(product));
    setEditMultianswerOptions([...(product.multianswer_options ?? [])]);
    setEditMultianswerDropdownIndication(product.multianswer_dropdown_indication ?? '');
    setEditFreeTextarea(product.multianswer_show_free_textarea);
    setEditStatus(product.status);
    setTimeout(() => firstInputRef.current?.focus(), 50);
  };

  const openBulkModal = () => {
    setBulkEdit(true);
    setModalProduct(null);
    setEditRedirectUrl('');
    setEditIndication('');
    setEditAnswerType('open');
    setEditMultianswerOptions([]);
    setEditMultianswerDropdownIndication('');
    setEditFreeTextarea(false);
    setEditStatus('active');
    setTimeout(() => firstInputRef.current?.focus(), 50);
  };

  const closeModal = () => {
    setModalProduct(null);
    setBulkEdit(false);
    setBulkConfirm(false);
    setSaving(false);
  };

  /* ── answer type change ── */
  /* moving to an open answer drops the closed-question data; single↔multi
     keeps the options the user already entered since the shape is identical */
  const handleAnswerTypeChange = (type: AnswerType) => {
    setEditAnswerType(prev => {
      if (prev === type) return prev;
      if (type === 'open') {
        setEditMultianswerOptions([]);
        setEditMultianswerDropdownIndication('');
        setEditFreeTextarea(false);
      }
      return type;
    });
  };

  /* ── answer option editing ── */
  const addOption = () => setEditMultianswerOptions(prev => [...prev, '']);

  const updateOption = (index: number, value: string) =>
    setEditMultianswerOptions(prev =>
      prev.map((o, i) => (i === index ? value : o))
    );

  const removeOption = (index: number) =>
    setEditMultianswerOptions(prev => prev.filter((_, i) => i !== index));

  /* a closed question needs at least two non-empty options to be answerable */
  const isClosed = editAnswerType === 'single' || editAnswerType === 'multi';
  const cleanedOptions = editMultianswerOptions
    .map(s => s.trim())
    .filter(Boolean);
  const optionsTooFew = isClosed && cleanedOptions.length < 2;

  /* a suspended product must redirect somewhere — block saving until a URL is entered */
  const suspendedWithoutUrl =
    editStatus === 'suspended' && !editRedirectUrl.trim();

  const cannotSave = suspendedWithoutUrl || optionsTooFew;

  const buildPayload = () => ({
    user_id: user?.id,
    status: editStatus,
    redirect_url: editStatus === 'suspended' ? editRedirectUrl.trim() : '',
    product_indication: editIndication,
    multianswer_options: cleanedOptions,
    multianswer_dropdown_indication: isClosed ? editMultianswerDropdownIndication.trim() : '',
    multianswer_show_free_textarea: editFreeTextarea,
    ...answerTypeToFlags(editAnswerType),
  });

  const handleSave = async () => {
    if (cannotSave) return;
    if (bulkEdit) {
      setBulkConfirm(true);
      return;
    }
    if (!modalProduct) return;
    setSaving(true);
    try {
      await axios.patch(
        `${backendBaseUrl}/employee/products/${modalProduct.id}/`,
        buildPayload()
      );
      closeModal();
      fetchProducts();
    } catch {
      setSaving(false);
    }
  };

  const executeBulkSave = async () => {
    if (cannotSave) return;
    setSaving(true);
    try {
      await Promise.all(
        [...selectedIds].map(id =>
          axios.patch(
            `${backendBaseUrl}/employee/products/${id}/`,
            buildPayload()
          )
        )
      );
      setSelectedIds(new Set());
      closeModal();
      fetchProducts();
    } catch {
      setSaving(false);
      setBulkConfirm(false);
    }
  };

  /* ── recover ── */
  const confirmRecover = (product: EmployeeProduct) =>
    setRecoverTarget({ id: product.id, name: product.name || product.code });

  const executeRecover = async () => {
    if (!recoverTarget) return;
    setRecovering(true);
    try {
      await axios.post(
        `${backendBaseUrl}/employee/products/${recoverTarget.id}/recover/`,
        { user_id: user?.id }
      );
      fetchProducts();
    } finally {
      setRecovering(false);
      setRecoverTarget(null);
    }
  };

  const cancelRecover = () => setRecoverTarget(null);

  /* ── selection ── */
  const toggleSelectAll = async () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visibleProducts.map(p => p.id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      });
      return;
    }
    setSelectingAll(true);
    try {
      const buildParams = (page: number) => {
        const p = new URLSearchParams();
        if (user?.id) p.append('user_id', String(user.id));
        if (selectedEventId) p.append('event_id', selectedEventId);
        if (filterStatus) p.append('status', filterStatus);
        if (filterType) p.append('answer_type', filterType);
        if (searchQuery) p.append('q', searchQuery);
        if (sortBy) {
          p.append('sort', sortBy);
          p.append('dir', sortDir);
        }
        p.append('page', String(page));
        p.append('page_size', '100');
        return p;
      };
      const first = await axios.get(
        `${backendBaseUrl}/employee/products/?${buildParams(1).toString()}`
      );
      const { total_pages } = first.data.pagination as PaginationInfo;
      let all: EmployeeProduct[] = first.data.products;
      if (total_pages > 1) {
        const rest = await Promise.all(
          Array.from({ length: total_pages - 1 }, (_, i) =>
            axios
              .get(
                `${backendBaseUrl}/employee/products/?${buildParams(i + 2).toString()}`
              )
              .then(r => r.data.products as EmployeeProduct[])
          )
        );
        all = all.concat(rest.flat());
      }
      setSelectedIds(new Set(all.map(p => p.id)));
    } catch {
      /* silent */
    } finally {
      setSelectingAll(false);
    }
  };

  const toggleSelect = (id: number) =>
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedEventId('');
    setFilterStatus('all');
    setFilterType('');
    setSortBy('');
    setSortDir('asc');
    setCurrentPage(1);
  };

  /* click a column header: asc → desc → unsorted */
  const handleSort = (key: SortKey) => {
    setCurrentPage(1);
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') setSortDir('desc');
    else {
      setSortBy('');
      setSortDir('asc');
    }
  };

  const visibleProducts = products;

  const allVisibleSelected =
    visibleProducts.length > 0 &&
    visibleProducts.every(p => selectedIds.has(p.id));
  const someVisibleSelected = visibleProducts.some(p => selectedIds.has(p.id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        someVisibleSelected && !allVisibleSelected;
    }
  });

  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterType]);

  const sortableHeader = (key: SortKey, label: string) => (
    <th
      className="mgmt-th--sortable"
      onClick={() => handleSort(key)}
      title={`Sort by ${label}`}
    >
      <span className="mgmt-th__sort">
        {label}
        {sortBy === key ? (
          sortDir === 'asc' ? (
            <FaSortUp size={11} />
          ) : (
            <FaSortDown size={11} />
          )
        ) : (
          <FaSort size={11} className="mgmt-th__sort-idle" />
        )}
      </span>
    </th>
  );

  /* ── render ── */
  return (
    <>
      <h2 className="mgmt-page-title">Questions</h2>

      {/* Filters */}
      <div className="mgmt-filters">
        <div className="mgmt-filters__search">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="mgmt-filters__search-input"
            placeholder="Search by name or code…"
            onKeyDown={e =>
              e.key === 'Enter' && (setCurrentPage(1), fetchProducts())
            }
          />
          <button
            className="mgmt-filters__search-btn"
            onClick={() => {
              setCurrentPage(1);
              fetchProducts();
            }}
          >
            🔍
          </button>
        </div>

        <div className="mgmt-filter-group">
          <label>Event</label>
          <select
            value={selectedEventId}
            onChange={e => {
              setSelectedEventId(e.target.value);
              setCurrentPage(1);
            }}
            className="mgmt-select"
            disabled={!events.length}
          >
            <option value="">All events</option>
            {events.map(e => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mgmt-filter-group">
          <label>Status</label>
          <select
            value={filterStatus}
            onChange={e => {
              setFilterStatus(
                e.target.value as
                  | ''
                  | 'all'
                  | 'active'
                  | 'suspended'
                  | 'deactivated'
              );
              setCurrentPage(1);
            }}
            className="mgmt-select"
          >
            <option value="all">All</option>
            <option value="">Active + Suspended</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="deactivated">Deactivated</option>
          </select>
        </div>

        <div className="mgmt-filter-group">
          <label>Answer type</label>
          <select
            value={filterType}
            onChange={e => {
              setFilterType(e.target.value as '' | AnswerType);
              setCurrentPage(1);
            }}
            className="mgmt-select"
          >
            <option value="">All types</option>
            <option value="open">Open</option>
            <option value="single">Closed - Single answer</option>
            <option value="multi">Closed - Multi answer</option>
          </select>
        </div>

        <div className="mgmt-filters__footer">
          <div className="mgmt-filters__footer-left">
            {selectedIds.size > 0 && (
              <button
                onClick={openBulkModal}
                className="mgmt-btn mgmt-btn--primary mgmt-btn--sm"
              >
                <FaPencilAlt size={11} /> Edit selected ({selectedIds.size})
              </button>
            )}
          </div>
          <button
            onClick={resetFilters}
            className="mgmt-btn mgmt-btn--reset mgmt-btn--sm"
          >
            🧹 Reset filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mgmt-table-wrap">
        <table className="mgmt-table">
          <colgroup>
            <col className="col-check" />
            <col className="col-name" />
            <col className="col-event" />
            <col className="col-code" />
            <col className="col-url" />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
            <col className="col-status" />
            <col className="col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th className="col-check">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="mgmt-checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  disabled={selectingAll}
                />
              </th>
              {sortableHeader('name', 'Name')}
              {sortableHeader('event', 'Event')}
              {sortableHeader('code', 'Code')}
              {sortableHeader('redirect_url', 'Redirect URL')}
              {sortableHeader('answer_type', 'Answer type')}
              {sortableHeader('created_at', 'Created')}
              {sortableHeader('status', 'Status')}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleProducts.map(product => {
              const isDeactivated = product.status === 'deactivated';
              return (
                <tr
                  key={product.id}
                  className={isDeactivated ? 'mgmt-row--deactivated' : ''}
                >
                  <td>
                    <input
                      type="checkbox"
                      className="mgmt-checkbox"
                      checked={selectedIds.has(product.id)}
                      onChange={() => toggleSelect(product.id)}
                      disabled={isDeactivated}
                    />
                  </td>
                  <td
                    className="mgmt-td-truncate"
                    title={product.name}
                    style={{ fontWeight: 600, color: 'var(--mgmt-gray-900)' }}
                  >
                    {product.name || '—'}
                  </td>
                  <td
                    className="mgmt-td-truncate"
                    title={product.event_name || ''}
                  >
                    {product.event_name ? (
                      <span className="mgmt-badge mgmt-badge--primary">
                        {product.event_name}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--mgmt-gray-400)' }}>—</span>
                    )}
                  </td>
                  <td className="mgmt-td-truncate" title={product.code}>
                    <span className="cell-mono">{product.code}</span>
                  </td>
                  <td
                    className="mgmt-td-truncate"
                    title={product.redirect_url || ''}
                  >
                    {product.redirect_url ? (
                      <a
                        href={product.redirect_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mgmt-link"
                      >
                        {product.redirect_url}
                      </a>
                    ) : (
                      <span style={{ color: 'var(--mgmt-gray-400)' }}>—</span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const at = getAnswerType(product);
                      const meta = ANSWER_TYPE_META[at];
                      return (
                        <span className={meta.cls}>
                          {meta.label}
                          {product.multianswer_show_free_textarea && (
                            <span
                              title="Free textarea enabled"
                              style={{ marginLeft: '0.3em', opacity: 0.7 }}
                            >
                              ✏️
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td
                    style={{
                      whiteSpace: 'nowrap',
                      color: 'var(--mgmt-gray-600)',
                    }}
                  >
                    {product.created_at
                      ? new Date(product.created_at).toLocaleDateString(
                          'it-IT',
                          {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          }
                        )
                      : '—'}
                  </td>
                  <td>
                    <span className={STATUS_META[product.status].cls}>
                      {STATUS_META[product.status].label}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      {isDeactivated ? (
                        <button
                          onClick={() => confirmRecover(product)}
                          className="mgmt-btn mgmt-btn--outline mgmt-btn--sm"
                          title="Recover question"
                        >
                          <FaUndo size={10} /> Recover
                        </button>
                      ) : (
                        <button
                          onClick={() => openModal(product)}
                          className="mgmt-btn mgmt-btn--outline mgmt-btn--sm"
                          title="Edit"
                        >
                          <FaPencilAlt size={11} /> Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {visibleProducts.length === 0 && (
              <tr>
                <td colSpan={9} className="mgmt-table__empty">
                  <span
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: '1.5rem',
                    }}
                  >
                    📦
                  </span>
                  No questions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {pagination && (
          <div className="mgmt-pagination">
            <div className="mgmt-pagination__info">
              <span className="mgmt-pagination__count">
                {pagination.total_count} question
                {pagination.total_count !== 1 ? 's' : ''}
              </span>
              <label className="mgmt-pagination__page-size">
                Rows
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[20, 50, 100].map(n => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div
              className="mgmt-pagination__pages"
              style={{
                visibility: pagination.total_pages > 1 ? 'visible' : 'hidden',
              }}
            >
              <button
                className="mgmt-pagination__btn"
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                <FaChevronLeft size={11} /> Prev
              </button>
              <span className="mgmt-pagination__page-label">
                {currentPage} / {pagination.total_pages}
              </span>
              <button
                className="mgmt-pagination__btn"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === pagination.total_pages}
              >
                Next <FaChevronRight size={11} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {(modalProduct || bulkEdit) && (
        <div className="mgmt-modal-backdrop">
          <div className="mgmt-modal" role="dialog" aria-modal="true">
            <div className="mgmt-modal__header">
              <div>
                <h3 className="mgmt-modal__title">
                  {bulkEdit
                    ? `Edit ${selectedIds.size} question${selectedIds.size !== 1 ? 's' : ''}`
                    : modalProduct?.name || modalProduct?.code || '—'}
                </h3>
                <p className="mgmt-modal__subtitle">
                  {bulkEdit
                    ? 'Changes will be applied to all selected questions.'
                    : [modalProduct?.brand_name, modalProduct?.event_name]
                        .filter(Boolean)
                        .join(' › ')}
                </p>
              </div>
              <button
                className="mgmt-modal__close"
                onClick={closeModal}
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="mgmt-modal__body">
              {!bulkEdit && modalProduct && (
                <>
                  <div className="mgmt-modal__summary">
                    <div className="mgmt-modal__summary-row">
                      <span className="mgmt-modal__summary-label">Name</span>
                      <span style={{ fontWeight: 600 }}>
                        {modalProduct.name || '—'}
                      </span>
                    </div>
                    <div className="mgmt-modal__summary-row">
                      <span className="mgmt-modal__summary-label">Event</span>
                      <span>
                        {modalProduct.event_name ? (
                          <span className="mgmt-badge mgmt-badge--primary">
                            {modalProduct.event_name}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--mgmt-gray-400)' }}>
                            —
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="mgmt-modal__summary-row">
                      <span className="mgmt-modal__summary-label">Code</span>
                      <span className="cell-mono">{modalProduct.code}</span>
                    </div>
                    <div className="mgmt-modal__summary-row">
                      <span className="mgmt-modal__summary-label">
                        Redirect URL
                      </span>
                      <span>
                        {modalProduct.redirect_url ? (
                          <a
                            href={modalProduct.redirect_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mgmt-link"
                          >
                            {modalProduct.redirect_url}
                          </a>
                        ) : (
                          <span style={{ color: 'var(--mgmt-gray-400)' }}>
                            —
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="mgmt-modal__summary-row">
                      <span className="mgmt-modal__summary-label">
                        Answer type
                      </span>
                      <span>
                        {(() => {
                          const at = getAnswerType(modalProduct);
                          return (
                            <span className={ANSWER_TYPE_META[at].cls}>
                              {ANSWER_TYPE_META[at].label}
                            </span>
                          );
                        })()}
                      </span>
                    </div>
                    <div className="mgmt-modal__summary-row">
                      <span className="mgmt-modal__summary-label">Status</span>
                      <span className={STATUS_META[modalProduct.status].cls}>
                        {STATUS_META[modalProduct.status].label}
                      </span>
                    </div>
                  </div>

                  <div className="mgmt-modal__divider" />
                </>
              )}

              <div className="mgmt-form-field">
                <label className="mgmt-form-label">
                  Question text{' '}
                  <span className="mgmt-form-hint">(question indication)</span>
                </label>
                <RichTextEditor
                  editorRef={firstInputRef}
                  value={editIndication}
                  onChange={setEditIndication}
                  ariaLabel="Question text"
                  placeholder="Enter the question or indication shown to the user…"
                />
              </div>

              <div className="mgmt-form-field">
                <label className="mgmt-form-label">Answer type</label>
                <div className="mgmt-answer-type-group">
                  {(['open', 'single', 'multi'] as AnswerType[]).map(at => (
                    <button
                      key={at}
                      type="button"
                      className={`mgmt-answer-type-btn${editAnswerType === at ? ' mgmt-answer-type-btn--active' : ''}`}
                      onClick={() => handleAnswerTypeChange(at)}
                    >
                      <span
                        className={ANSWER_TYPE_META[at].cls}
                        style={{ pointerEvents: 'none' }}
                      >
                        {ANSWER_TYPE_META[at].label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {isClosed && (
                <div className="mgmt-form-field">
                  <label className="mgmt-form-label">
                    Dropdown indication{' '}
                    <span className="mgmt-form-hint">(optional label shown above the answer options)</span>
                  </label>
                  <input
                    type="text"
                    className="mgmt-form-control"
                    value={editMultianswerDropdownIndication}
                    onChange={e => setEditMultianswerDropdownIndication(e.target.value)}
                    placeholder="e.g. Select an option…"
                  />
                </div>
              )}

              {isClosed && (
                <div className="mgmt-form-field">
                  <label className="mgmt-form-label">
                    Answer options{' '}
                    <span className="mgmt-form-hint">
                      (
                      {editAnswerType === 'single'
                        ? 'the user picks one'
                        : 'the user can pick several'}
                      )
                    </span>
                  </label>
                  <div className="mgmt-options-editor">
                    {editMultianswerOptions.map((opt, i) => (
                      <div key={i} className="mgmt-option-row">
                        <span
                          className="mgmt-option-row__marker"
                          aria-hidden="true"
                        >
                          {editAnswerType === 'single' ? '○' : '☐'}
                        </span>
                        <input
                          type="text"
                          className="mgmt-form-control"
                          value={opt}
                          onChange={e => updateOption(i, e.target.value)}
                          placeholder={`Option ${i + 1}`}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (i === editMultianswerOptions.length - 1)
                                addOption();
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="mgmt-option-row__remove"
                          onClick={() => removeOption(i)}
                          title="Remove option"
                          aria-label={`Remove option ${i + 1}`}
                        >
                          <FaTimes size={11} />
                        </button>
                      </div>
                    ))}
                    {editMultianswerOptions.length === 0 && (
                      <p className="mgmt-form-hint" style={{ margin: 0 }}>
                        No options yet — add the choices users can select.
                      </p>
                    )}
                    <button
                      type="button"
                      className="mgmt-btn mgmt-btn--outline mgmt-btn--sm mgmt-option-add"
                      onClick={addOption}
                    >
                      <FaPlus size={10} /> Add option
                    </button>
                  </div>
                  {optionsTooFew && (
                    <p
                      className="mgmt-form-hint"
                      style={{ color: 'var(--mgmt-danger)' }}
                    >
                      Add at least two options to continue.
                    </p>
                  )}
                </div>
              )}

              <div className="mgmt-form-field">
                <label className="mgmt-form-label mgmt-form-label--checkbox">
                  <input
                    type="checkbox"
                    checked={editFreeTextarea}
                    onChange={e => setEditFreeTextarea(e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Show free-text answer field above the answer options
                </label>
              </div>

              <div className="mgmt-form-field">
                <label className="mgmt-form-label">Status</label>
                <div className="mgmt-answer-type-group">
                  {(
                    ['active', 'suspended', 'deactivated'] as ProductStatus[]
                  ).map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`mgmt-answer-type-btn${editStatus === s ? ' mgmt-answer-type-btn--active' : ''}`}
                      onClick={() => setEditStatus(s)}
                    >
                      <span
                        className={STATUS_META[s].cls}
                        style={{ pointerEvents: 'none' }}
                      >
                        {STATUS_META[s].label}
                      </span>
                    </button>
                  ))}
                </div>
                {editStatus !== 'suspended' && (
                  <p className="mgmt-form-hint">
                    {editStatus === 'active'
                      ? 'The question is active and visible to users.'
                      : 'The question is hidden from the main view and can be reactivated later.'}
                  </p>
                )}
              </div>

              {editStatus === 'suspended' && (
                <div className="mgmt-form-field">
                  <label className="mgmt-form-label">
                    Redirect URL{' '}
                    <span className="mgmt-form-hint">(required)</span>
                  </label>
                  <input
                    type="url"
                    className="mgmt-form-control"
                    value={editRedirectUrl}
                    onChange={e => setEditRedirectUrl(e.target.value)}
                    placeholder="https://…"
                    autoFocus
                  />
                  <p className="mgmt-form-hint">
                    A suspended question redirects users to this URL.
                    {suspendedWithoutUrl && (
                      <span
                        style={{
                          color: 'var(--mgmt-danger)',
                          marginLeft: '0.3rem',
                        }}
                      >
                        Enter a URL to continue.
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="mgmt-modal__footer">
              <button
                className="mgmt-btn mgmt-btn--outline"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="mgmt-btn mgmt-btn--primary"
                onClick={handleSave}
                disabled={saving || cannotSave}
                title={
                  suspendedWithoutUrl
                    ? 'Enter a redirect URL for the suspended status'
                    : optionsTooFew
                      ? 'Add at least two answer options'
                      : undefined
                }
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk edit confirm modal */}
      {bulkConfirm && (
        <div className="mgmt-modal-backdrop">
          <div
            className="mgmt-modal mgmt-modal--confirm"
            role="dialog"
            aria-modal="true"
          >
            <div className="mgmt-modal__header">
              <h3 className="mgmt-modal__title">Confirm bulk edit</h3>
              <button
                className="mgmt-modal__close"
                onClick={() => setBulkConfirm(false)}
                disabled={saving}
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="mgmt-modal__body">
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--mgmt-fs-sm)',
                  color: 'var(--mgmt-gray-700)',
                }}
              >
                Are you sure you want to modify{' '}
                <strong>
                  {selectedIds.size} question{selectedIds.size !== 1 ? 's' : ''}
                </strong>{' '}
                with the entered data?
              </p>
              <p
                style={{
                  margin: '0.5rem 0 0',
                  fontSize: 'var(--mgmt-fs-xs)',
                  color: 'var(--mgmt-gray-500)',
                }}
              >
                This will overwrite the question text, answer type, free
                textarea and status for every selected question.
              </p>
            </div>
            <div className="mgmt-modal__footer">
              <button
                className="mgmt-btn mgmt-btn--outline"
                onClick={() => setBulkConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="mgmt-btn mgmt-btn--primary"
                onClick={executeBulkSave}
                disabled={saving}
              >
                {saving
                  ? 'Saving…'
                  : `Modify ${selectedIds.size} question${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recover confirm modal */}
      {recoverTarget && (
        <div className="mgmt-modal-backdrop">
          <div
            className="mgmt-modal mgmt-modal--confirm"
            role="dialog"
            aria-modal="true"
          >
            <div className="mgmt-modal__header">
              <h3 className="mgmt-modal__title">Recover question</h3>
              <button
                className="mgmt-modal__close"
                onClick={cancelRecover}
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="mgmt-modal__body">
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--mgmt-fs-sm)',
                  color: 'var(--mgmt-gray-700)',
                }}
              >
                Recover <strong>{recoverTarget.name}</strong> and set it back to{' '}
                <strong>Active</strong>?
              </p>
            </div>
            <div className="mgmt-modal__footer">
              <button
                className="mgmt-btn mgmt-btn--outline"
                onClick={cancelRecover}
                disabled={recovering}
              >
                Cancel
              </button>
              <button
                className="mgmt-btn mgmt-btn--primary"
                onClick={executeRecover}
                disabled={recovering}
              >
                {recovering ? 'Recovering…' : 'Recover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Products;
